import { adjustmentRequest, loadNoteSources } from "utils/adjustmentNoteAPI";
import { noteAmountAllowed } from "utils/noteReview";
import { calculateNoteLines } from "utils/notePricing";
import useUnsavedChanges from "hooks/useUnsavedChanges";
import { useRef } from "react";
import { Button, CircularProgress, Dialog } from "@mui/material";
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LuChevronRight,
  LuFileText,
  LuInfo,
  LuPlus,
  LuSave,
  LuSearch,
  LuTrash2,
  LuX,
} from "react-icons/lu";
import InvoiceSelect from "components/FormControls/ThemedSelect";
import DebitNoteResponsiveHeader from "./DebitNoteResponsiveHeader";
import CreditLinesViewport from "../CreditNote/CreditLinesViewport";
import { CardSkeleton } from "components/Skeleton";

import "./debitNote.css";
import "../AdjustmentNotes/adjustmentNotes.css";
import "../AdjustmentNotes/noteDesign.css";
import "./debitAdjustmentLines.css";
import "./debitDesign.css";

const CUSTOMER_REASONS = ["Additional charges", "Price correction"];
const SUPPLIER_REASONS = ["Faulty return", "Overbilled"];

function readLoginDetails() {
  try {
    return JSON.parse(sessionStorage.getItem("LOGIN_DETAILS") || "null");
  } catch {
    return null;
  }
}

const newLine = (id) => ({
  id,
  description: "",
  quantity: "1",
  unitAmount: "",
  gst: "10",
});
const cleanForm = () => ({
  source: "",
  reason: "",
  notes: "",
  lines: [newLine(1)],
});
const createOperationKey = () =>
  window.crypto?.randomUUID?.() ||
  `note-${Date.now()}-${Math.random().toString(36).slice(2)}`;

function Modal({ title, children, actions, onClose, credit = false }) {
  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby="debit-modal-title"
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: `debit-modal${credit ? " credit-confirm-modal" : " debit-design-modal"}` }}
    >
      <h2 id="debit-modal-title">{title}</h2>
      <div className="debit-modal-copy">{children}</div>
      <div className="debit-modal-actions">{actions}</div>
    </Dialog>
  );
}

function EditorActions({
  customer,
  credit,
  onCancel,
  onSubmit,
  onSave,
  busy,
  busyAction,
  disabled = false,
  location = "top",
}) {
  return (
    <div className={`debit-editor-actions debit-editor-actions-${location}`}>
      {onCancel && (
        <Button
          className="debit-secondary-button"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </Button>
      )}
      <Button
        className="debit-secondary-button"
        disabled={busy || disabled}
        onClick={onSave}
      >
        {busy && busyAction === "save" ? (
          <CircularProgress size={16} color="inherit" aria-hidden="true" />
        ) : (
          <LuSave aria-hidden="true" />
        )}
        {busy && busyAction === "save" ? "Saving…" : "Save draft"}
      </Button>
      <Button
        className="debit-primary-button"
        disabled={busy || disabled}
        onClick={onSubmit}
      >
        {busy && busyAction === "apply" && (
          <CircularProgress size={16} color="inherit" aria-hidden="true" />
        )}
        {busy && busyAction === "apply"
          ? credit
            ? "Applying…"
            : customer
            ? "Issuing…"
            : "Notifying…"
          : credit
          ? "Apply credit"
          : customer
          ? "Issue debit note"
          : "Notify supplier"}
      </Button>
    </div>
  );
}

function DebitPreview({
  customer,
  credit,
  selectedSource,
  lines = [],
  total,
  currency,
  formatMoney,
  valid,
  responsivePlacement = "",
}) {
  const balance = selectedSource?.balance || 0;
  const projected =
    credit || !customer ? Math.max(0, balance - total) : balance + total;
  const compactMoney = (value) =>
    formatMoney(value).replace(` ${currency}`, "");

  return (
    <aside
      className={`debit-preview${
        responsivePlacement
          ? ` debit-preview-responsive debit-preview-${responsivePlacement}`
          : " debit-preview-desktop"
      }`}
    >
      <h2>
        {customer ? "Invoice balance preview" : "Supplier request preview"}
      </h2>
      <p>
        Informational only ·{" "}
        {customer
          ? credit
            ? "Credit has not been applied."
            : "Debit note has not been issued."
          : "No financial posting yet."}
      </p>
      {selectedSource ? (
        <>
          <div className="debit-preview-card debit-preview-source-card">
            <strong>
              <LuFileText aria-hidden="true" /> {selectedSource.reference}
            </strong>
            <b>{selectedSource.contact || "Contact not recorded"}</b>
            <span>
              {currency} · Tax treatment inherited from linked{" "}
              {customer ? "invoice" : selectedSource.kind.toUpperCase()}
            </span>
          </div>
          <div className="debit-preview-card debit-preview-breakdown">
            <div>
              <span>
                {customer
                  ? credit
                    ? "Outstanding balance before credit"
                    : "Outstanding balance before debit"
                  : "Recorded amount owed"}
              </span>
              <strong>{compactMoney(balance)}</strong>
            </div>
            {credit && (
              <div className="note-preview-lines">
                {lines.map((line, index) => (
                  <p key={index}>
                    <span>
                      {line.description || "Credit line"} × {line.quantity}
                    </span>
                    <strong>−{compactMoney(line.total)}</strong>
                  </p>
                ))}
              </div>
            )}
            <div>
              <span>
                {customer
                  ? credit
                    ? "Credit deduction"
                    : "Customer charge"
                  : "Requested reduction"}
              </span>
              <strong>
                {customer && !credit ? "+" : "-"}
                {compactMoney(total)}
              </strong>
            </div>
            <hr />
            <div className="debit-preview-projected">
              <span>
                {customer
                  ? credit
                    ? "Projected remaining balance"
                    : "Projected balance after issue"
                  : "Projected amount owed if confirmed"}
              </span>
              <strong>
                {valid && (customer || total <= balance)
                  ? formatMoney(projected)
                  : "Unavailable"}
              </strong>
            </div>
          </div>
        </>
      ) : (
        <div className="debit-preview-card debit-preview-empty-card">
          <strong>
            {customer ? "Against invoice" : "Against expense / eligible RCTI"}
          </strong>
          <span>
            {customer
              ? "Choose an eligible invoice to see the customer and balance."
              : "Choose an eligible expense or RCTI to see the supplier and allowance."}
          </span>
        </div>
      )}
      <div className="debit-preview-card debit-preview-callout">
        <LuInfo aria-hidden="true" />
        <span>
          {customer
            ? credit
              ? `Eligible credit allowance: ${formatMoney(
                  selectedSource?.allowance || 0
                )}. Prior posted credits reduce this allowance; it is separate from the unpaid balance.`
              : "Customer charges increase the invoice balance. Review prior adjustments to avoid duplicate charges; the unpaid balance is not a charge limit."
            : "Eligible reduction allowance accounts for prior posted adjustments and is separate from the recorded amount owed. Over-reduction is blocked."}
        </span>
      </div>
      {!customer && (
        <div className="debit-preview-card debit-preview-callout debit-preview-notification">
          <LuInfo aria-hidden="true" />
          <span>
            Notification sends a request without posting. Posting requires a
            validated confirmation or a linked supplier adjustment.
          </span>
        </div>
      )}
    </aside>
  );
}

export default function DebitNoteEditor({ credit = false }) {
  const kind = credit ? "credit" : "debit";
  const base = credit ? "/creditNote" : "/debitNote";
  const debitRequest = (path, body, method) =>
    adjustmentRequest(kind, path, body, method);
  const creationKey = useRef(createOperationKey());
  const lineSequence = useRef(1000);
  const [acknowledged, setAcknowledged] = useState(false);
  const [baseline, setBaseline] = useState(JSON.stringify(cleanForm()));
  const [sourceQuery, setSourceQuery] = useState("");
  const [controller] = useMaterialUIController();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [saved, setSaved] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(Boolean(id));
  const [draftLoadError, setDraftLoadError] = useState(false);
  const [draftRequestKey, setDraftRequestKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const [actionError, setActionError] = useState("");
  const loginDetails = useMemo(readLoginDetails, []);
  const initialDirection =
    new URLSearchParams(location.search).get("direction") === "supplier"
      ? "supplier"
      : "customer";
  const [direction, setDirection] = useState(initialDirection);
  const [form, setForm] = useState(cleanForm);
  const selectedSourceID = useRef(form.source);
  selectedSourceID.current = form.source;
  const [nextDirection, setNextDirection] = useState(null);
  const [dialog, setDialog] = useState("");

  const [errorsVisible, setErrorsVisible] = useState(false);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [sourceLoading, setSourceLoading] = useState(true);
  const [sourceLoadError, setSourceLoadError] = useState(false);
  const hasEdits = JSON.stringify(form) !== baseline;
  const saveAndExitRef = useRef(null);
  const unsaved = useUnsavedChanges(hasEdits && !busy, {
    documentName: credit ? "credit note" : "debit note",
    isExistingDraft: Boolean(id || saved?._id),
    canSaveDraft: !saved || (credit ? saved.activityStatus : saved.status) === "Draft",
    onSave: () => saveAndExitRef.current?.(),
  });

  useEffect(() => {
    let active = true;
    setSourceLoading(true);
    setSourceLoadError(false);

    const publishSources = (sources) => {
      if (active)
        setSourceOptions((current) => [
          ...sources,
          ...current.filter(
            (row) =>
              row.id === selectedSourceID.current &&
              !sources.some((next) => next.id === row.id)
          ),
        ]);
    };
    const loadSources = () =>
      loadNoteSources(credit ? "customer" : direction, {
        q: sourceQuery,
        onPage: publishSources,
      })
        .then(publishSources)
        .catch(() => {
          if (active) setSourceLoadError(true);
        })
        .finally(() => {
          if (active) setSourceLoading(false);
        });
    // Fetch initial choices immediately; debounce only a typed search.
    const timer = sourceQuery ? setTimeout(loadSources, 250) : null;
    if (!sourceQuery) loadSources();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [direction, loginDetails, credit, sourceQuery]);

  useEffect(() => {
    let active = true;
    if (id) {
      setLoadingDraft(true);
      setDraftLoadError(false);
      setActionError("");
      debitRequest(`/${id}`, undefined, "get")
        .then(({ note }) => {
          if (!active) return;
          if ((credit ? note.activityStatus : note.status) !== "Draft") {
            navigate(`${base}/view/${id}`, { replace: true });
            return;
          }
          setSaved(note);
          setDirection(credit ? "customer" : note.direction);
          const loaded = {
            source: String(credit ? note.sourceInvoiceID : note.sourceID),
            reason: note.reason,
            notes: (credit ? note.internalNotes : note.notes) || "",
            lines: note.lines.map((line, index) => ({
              ...line,
              id: index + 1,
              quantity: String(line.quantity),
              unitAmount: Number(line.unitAmount).toFixed(2),
            })),
          };
          setForm(loaded);
          setBaseline(JSON.stringify(loaded));
          loadNoteSources(credit ? "customer" : note.direction, {
            id: credit ? note.sourceInvoiceID : note.sourceID,
          })
            .then((rows) =>
              setSourceOptions((current) => [
                ...current.filter(
                  (row) => !rows.some((next) => next.id === row.id)
                ),
                ...rows,
              ])
            )
            .catch(() => setSourceLoadError(true));
        })
        .catch((e) => {
          if (!active) return;
          setDraftLoadError(true);
          setActionError(e.response?.data?.Result_Message || e.message);
        })
        .finally(() => { if (active) setLoadingDraft(false); });
    } else setLoadingDraft(false);
    return () => { active = false; };
    // The route controls loading; draft changes do not reload the saved document.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, credit, draftRequestKey]);

  const currency =
    sourceOptions.find((source) => String(source.id) === String(form.source))
      ?.currency ||
    (/^[A-Z]{3}$/.test(loginDetails?.currencyCode || "")
      ? loginDetails.currencyCode
      : "AUD");
  const organisation =
    loginDetails?.businessName ||
    loginDetails?.companyName ||
    loginDetails?.organisationName ||
    loginDetails?.organizationName ||
    "Your business";
  const customer = credit || direction === "customer";
  const reasons = credit
    ? [
        "Returned fittings",
        "Pricing correction",
        "Duplicate line",
        ...(!["Returned fittings", "Pricing correction", "Duplicate line"].includes(form.reason) && form.reason ? [form.reason] : []),
      ]
    : customer
    ? CUSTOMER_REASONS
    : SUPPLIER_REASONS;
  const selectedSource = sourceOptions.find(
    (source) => String(source.id) === String(form.source)
  );
  const calculation = calculateNoteLines(form.lines);
  // Keep completed amounts visible while a new line is still incomplete.
  // Save/issue validation continues to use the complete calculation below.
  const lineCalculations =
    !calculation.valid
      ? form.lines.map((line) => calculateNoteLines([line]))
      : null;
  const total = lineCalculations
    ? calculateNoteLines(
        form.lines.filter((_, index) => lineCalculations[index].valid)
      ).total
    : calculation.total;
  const amountValid =
    calculation.valid && calculation.lines.every((line) => line.description);
  const isValid =
    Boolean(form.source && form.reason) &&
    amountValid &&
    noteAmountAllowed(selectedSource, total, credit || !customer);
  const sourceTitle = customer
    ? "Against invoice"
    : "Against expense / eligible RCTI";
  const sourcePlaceholder = customer
    ? "Select an issued invoice"
    : "Select an expense or eligible RCTI";
  const helpText = customer
    ? "Search by invoice number or customer name."
    : "Search by expense number, eligible RCTI or supplier name. Customer invoices are excluded from supplier requests.";
  const formatMoney = (amount) => {
    const formatted = new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
    return `${formatted} ${currency}`;
  };

  const updateLine = (id, key, value) =>
    setForm((current) => ({
      ...current,
      lines: current.lines.map((line) =>
        line.id === id ? { ...line, [key]: value } : line
      ),
    }));
  const addLine = () =>
    setForm((current) => ({
      ...current,
      lines: [...current.lines, newLine(++lineSequence.current)],
    }));
  const removeLine = (id) =>
    setForm((current) => ({
      ...current,
      lines: current.lines.length === 1
        ? [newLine(id)]
        : current.lines.filter((line) => line.id !== id),
    }));
  const switchTo = (value) => {
    if (value === direction) return;
    if (hasEdits || saved) {
      setNextDirection(value);
      setDialog("direction");
      return;
    }
    setDirection(value);
    setForm(cleanForm());
  };
  const confirmSwitch = () => {
    setSaved(null);
    creationKey.current = createOperationKey();
    setDirection(nextDirection);
    setSourceQuery("");
    setForm(cleanForm());
    setBaseline(JSON.stringify(cleanForm()));
    setNextDirection(null);
    setDialog("");
  };
  const cancelEditor = () => navigate(base);
  const persist = async (leave = true, keepBusy = false, action = "save") => {
    setErrorsVisible(true);
    if (!isValid || !selectedSource || busy || loadingDraft || draftLoadError) return;
    setBusy(true);
    setBusyAction(action);
    setActionError("");
    try {
      const note = await debitRequest(saved ? `/${saved._id}` : "", {
        ...form,
        creationKey: creationKey.current,
        direction,
        sourceID: selectedSource.id,
        sourceKind: selectedSource.kind,
        revision: saved?.revision,
      });
      setSaved(note);
      setBaseline(JSON.stringify(form));
      if (leave) unsaved.markSaved();
      if (leave) navigate(`${base}/view/${note._id}`, { state: { noticeType: "success", notice: `Draft saved for ${selectedSource.reference} · ${selectedSource.contact}. ${credit || customer ? "Invoice balance" : "Recorded amount owed"} unchanged: ${formatMoney(selectedSource.balance)}.${!customer ? " No supplier notification has been sent." : ""}` } });
      return note;
    } catch (e) {
      setActionError(e.response?.data?.Result_Message || e.message);
    } finally {
      if (!keepBusy) {
        setBusy(false);
        setBusyAction("");
      }
    }
  };
  saveAndExitRef.current = async () => Boolean(await persist(false));
  const attemptAction = () => {
    if (loadingDraft || draftLoadError) return;
    setErrorsVisible(true);
    if (isValid) {
      setAcknowledged(false);
      setDialog("issue");
    }
  };
  const approveIssue = async () => {
    if (busy) return;
    const note = await persist(false, true, "apply");
    if (!note) {
      setBusy(false);
      return;
    }
    setBusy(true);
    try {
      await debitRequest(
        `/${note._id}/${credit ? "apply" : customer ? "issue" : "notify"}`,
        {
          confirm: true,
          revision: note.revision,
          expectedBalance: selectedSource.balance,
          sourceRevision: selectedSource.revision,
        }
      );
      unsaved.markSaved();
      navigate(`${base}/view/${note._id}`, {
        state: {
          noticeType: "success",
          notice: credit
            ? "Credit applied. The original invoice amount remains unchanged."
            : customer
            ? "Debit note issued. The supplementary charge was recorded; no payment was recorded."
            : "Supplier request sent. Awaiting confirmation; the payable remains unchanged.",
        },
      });
    } catch (e) {
      setActionError(e.response?.data?.Result_Message || e.message);
      try {
        const current = await debitRequest(`/${note._id}`, undefined, "get");
        setSaved(current.note);
        if (
          (credit ? current.note.activityStatus : current.note.status) !==
          "Draft"
        ) {
          unsaved.markSaved();
          navigate(`${base}/view/${note._id}`, {
            state: { notice: e.response?.data?.Result_Message || e.message },
          });
        }
      } catch (_) {
        /* Keep the saved draft visible; do not repeat posting. */
      }
    } finally {
      setBusy(false);
      setBusyAction("");
    }
  };
  const fieldError = (key, message) =>
    errorsVisible && !form[key] ? (
      <span className="debit-field-error">{message}</span>
    ) : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <main
        className={`debit-page debit-editor-page${credit ? " credit-editor-page" : " debit-design-editor"}${
          controller.darkMode ? " dark" : ""
        }`}
      >
        <DebitNoteResponsiveHeader
          organisation={organisation}
          role={loginDetails?.roleName || loginDetails?.userType || "Owner"}
        />
        <div className="debit-editor-topbar">
          <nav className="debit-breadcrumbs" aria-label="Breadcrumb">
            <Link to={base}>{credit ? "Credit note" : "Debit note"}</Link>
            <LuChevronRight aria-hidden="true" />
            <span>{id ? `Edit ${kind} note` : `New ${kind} note`}</span>
          </nav>
          <EditorActions
            credit={credit}
            customer={customer}
            onCancel={cancelEditor}
            onSubmit={attemptAction}
            onSave={() => persist()}
            busy={busy}
            busyAction={busyAction}
            disabled={loadingDraft || draftLoadError}
          />
        </div>

        {actionError && (
          <p className="debit-backend-notice" role="alert">
            {actionError}
            {selectedSource && !draftLoadError && (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  loadNoteSources(credit ? "customer" : direction, {
                    id: selectedSource.id,
                  })
                    .then((rows) => {
                      setSourceOptions((current) => [
                        ...current.filter(
                          (row) => row.id !== selectedSource.id
                        ),
                        ...rows,
                      ]);
                      setActionError(
                        rows.length
                          ? "Linked document refreshed. Review the updated balance before retrying."
                          : "This source is no longer eligible. Choose another document."
                      );
                    })
                    .catch(() =>
                      setActionError(
                        "Unable to refresh the linked document. Your edits are retained."
                      )
                    )
                }
              >
                Refresh linked document
              </button>
            )}
          </p>
        )}

        {draftLoadError && <button type="button" className="debit-secondary-button" onClick={() => setDraftRequestKey((current) => current + 1)}>Retry loading draft</button>}
        <div className={`debit-editor-layout${loadingDraft ? " is-loading" : ""}`} style={draftLoadError ? { display: "none" } : undefined}>
          <fieldset disabled={busy || draftLoadError || loadingDraft} className="debit-editor-form">
            <div className="debit-editor-title">
              <h1>
                {id ? `Edit ${kind} note` : `New ${kind} note`}
                {!customer && (
                  <span className="debit-tablet-direction-title">
                    {" "}
                    — To supplier
                  </span>
                )}
              </h1>
              <p>
                {organisation} ·{" "}
                {customer ? "Issued invoices" : "Supplier request"} · {currency}
              </p>
            </div>
            {loadingDraft && <div className="debit-editor-loading" role="status" aria-label={`Loading ${kind} note draft`}><CardSkeleton /></div>}
            {!loadingDraft && <>
            {!credit && (
              <section className="debit-form-section debit-direction-section">
                <label>Direction</label>
                <div
                  className="debit-direction-toggle"
                  role="group"
                  aria-label="Debit note direction"
                >
                  <button
                    type="button"
                    className={customer ? "is-active" : ""}
                    onClick={() => switchTo("customer")}
                  >
                    To customer
                  </button>
                  <button
                    type="button"
                    className={!customer ? "is-active" : ""}
                    onClick={() => switchTo("supplier")}
                  >
                    To supplier
                  </button>
                </div>
                <p
                  className={`debit-section-hint${
                    customer ? " debit-customer-direction-hint" : ""
                  }`}
                >
                  {customer
                    ? "Customer debit note issue an upward adjustment to the customer balance. Delivery is separate from posting."
                    : "Supplier requests notify the supplier and do not post a financial adjustment until a confirmed reduction is reviewed and applied. Recorded amount owed remains unchanged until a valid supplier adjustment is confirmed."}
                </p>
              </section>
            )}
            <section className="debit-form-section">
              {!credit && !customer && <label className="debit-mobile-supplier">Supplier
                <InvoiceSelect aria-label="Supplier" dark={controller.darkMode} value={selectedSource?.contactID || ""} displayValue={selectedSource?.contact || "Select supplier"} disabled={busy || sourceLoading} onChange={(event) => {
                  const source = sourceOptions.find((row) => row.contactID === event.target.value);
                  if (source) { setSourceQuery(""); setForm((current) => ({ ...current, source: source.id })); }
                }}>
                  <option value="" disabled>Select supplier</option>
                  {sourceOptions.filter((row, index, rows) => row.contactID && rows.findIndex((other) => other.contactID === row.contactID) === index).map((row) => <option key={row.contactID} value={row.contactID}>{row.contact}</option>)}
                </InvoiceSelect>
              </label>}
              <label htmlFor="debit-source">{sourceTitle}</label>
              <div className="debit-source-select-wrap">
                {customer ? (
                  <LuFileText aria-hidden="true" />
                ) : (
                  <LuSearch aria-hidden="true" />
                )}
                <InvoiceSelect
                  contactType="document"
                  onSearchChange={setSourceQuery}
                  id="debit-source"
                  className="debit-source-select"
                  dark={controller.darkMode}
                  aria-label={sourceTitle}
                  aria-invalid={errorsVisible && !form.source}
                  aria-describedby="debit-source-help"
                  value={form.source}
                  onChange={(event) => {
                    const source = event.target.value;
                    setSourceQuery("");
                    setForm((current) => ({
                      ...current,
                      source,
                    }));
                  }}
                  displayValue={
                    selectedSource
                      ? selectedSource.reference
                      : credit && form.source && saved?.sourceSnapshot?.reference ? saved.sourceSnapshot.reference : sourcePlaceholder
                  }
                  disabled={busy}
                >
                  <option value="" disabled={sourceLoading || Boolean(sourceOptions.length)}>
                    {sourceOptions.length
                      ? sourcePlaceholder
                      : sourceLoading
                      ? "Loading eligible documents…"
                      : "No eligible documents found"}
                  </option>
                  {sourceOptions.map((source) => (
                    <option
                      value={source.id}
                      key={`${source.kind}-${source.id}`}
                    >
                      {source.reference} ·{" "}
                      {source.contact || "Contact not recorded"}
                    </option>
                  ))}
                </InvoiceSelect>
              </div>
              <p id="debit-source-help" className="debit-field-hint">
                {helpText}
              </p>
              {fieldError(
                "source",
                customer
                  ? "Choose an eligible issued invoice to continue."
                  : "Choose an eligible expense or RCTI to continue."
              )}
              {form.source && !selectedSource && !sourceLoading && !sourceLoadError && (
                <span className="debit-field-error" role="alert">
                  {customer ? "This invoice is unavailable for selection. Choose another issued customer invoice." : "This expense or RCTI is unavailable for selection. Choose another eligible supplier document."} No adjustment can be posted until an eligible document is selected.
                </span>
              )}
              {sourceLoadError && (
                <span className="debit-field-error" role="alert">
                  Eligible documents could not be loaded. Refresh and try again.
                </span>
              )}
              {selectedSource && (
                <div className="debit-selected-source">
                  <div>
                    <strong>
                      <LuFileText aria-hidden="true" />{" "}
                      {selectedSource.reference}
                    </strong>
                    <span>
                      {selectedSource.contact || "Contact not recorded"}
                    </span>
                  </div>
                  <div>
                    <span>
                      {customer
                        ? credit
                          ? "Outstanding balance"
                          : "Current customer balance"
                        : "Recorded amount owed"}
                    </span>
                    <strong>{formatMoney(selectedSource.balance)}</strong>
                  </div>
                </div>
              )}
            </section>

            <section className={`debit-form-section debit-lines-section${credit ? " credit-lines-section" : " debit-adjustment-lines"}`}>
              <div className="debit-section-header">
                <h2>
                  <span className="debit-lines-title-desktop">
                    {credit ? "Credit lines" : "Adjustment lines"}
                  </span>
                  <span className="debit-lines-title-mobile">
                    {credit ? "Credit lines" : "Debit lines"}
                  </span>
                </h2>
              </div>
              <div className="debit-lines-table">
                <div className="debit-line-head">
                  <span>Description</span>
                  <span>Qty</span>
                  <span>{credit ? "Unit credit" : "Unit adjustment"}</span>
                  <span>Line total</span>
                  <span>Remove</span>
                </div>
                <CreditLinesViewport label={credit ? "Credit lines" : "Adjustment lines"}>
                {form.lines.map((line, index) => (
                  <div className="debit-line-row" key={line.id}>
                    <span className="debit-mobile-line-label">
                      Line {index + 1}
                    </span>
                    <div className="debit-description">
                      <span className="sr-only">Description</span>
                      <input
                        aria-label={`Description for ${kind} line ${index + 1}`}
                        maxLength={credit ? 2000 : undefined}
                        aria-invalid={credit && errorsVisible && !line.description.trim()}
                        value={line.description}
                        onChange={(event) =>
                          updateLine(line.id, "description", event.target.value)
                        }
                        placeholder="Describe this adjustment"
                      />
                    </div>
                    <label className="debit-quantity">
                      <span>Qty</span>
                      <input
                        inputMode="decimal"
                        aria-label={`Quantity for line ${index + 1}`}
                        value={line.quantity}
                        onChange={(event) =>
                          updateLine(line.id, "quantity", event.target.value)
                        }
                      />
                    </label>
                    <label className="debit-unit-amount">
                      <span>
                        {credit
                          ? "Amount"
                          : customer
                          ? "Unit debit"
                          : "Unit adjustment"}{" "}
                        ({currency})
                      </span>
                      <div className="note-unit-field">
                        <span aria-hidden="true">
                          {
                            new Intl.NumberFormat("en-AU", {
                              style: "currency",
                              currency,
                              currencyDisplay: "narrowSymbol",
                            })
                              .formatToParts(0)
                              .find((part) => part.type === "currency")?.value
                          }
                        </span>
                        <input
                          inputMode="decimal"
                          aria-label={`Unit amount for line ${index + 1}`}
                          value={line.unitAmount}
                          onChange={(event) =>
                            updateLine(
                              line.id,
                              "unitAmount",
                              event.target.value
                            )
                          }
                          placeholder="0.00"
                          aria-invalid={errorsVisible && !amountValid}
                          onBlur={() => {
                            if (
                              /^\d+(\.\d{1,2})?$/.test(String(line.unitAmount))
                            )
                              updateLine(
                                line.id,
                                "unitAmount",
                                Number(line.unitAmount).toFixed(2)
                              );
                          }}
                        />
                      </div>
                    </label>
                    <div className="debit-line-total">
                      <span>Line total</span>
                      <strong>
                        {formatMoney(
                          (lineCalculations
                            ? lineCalculations[index].total
                            : calculation.lines[index]?.total) || 0
                        ).replace(` ${currency}`, "")}
                      </strong>
                    </div>
                    <button
                      className="debit-remove-line"
                      type="button"
                      aria-label={`Remove adjustment line ${index + 1}`}
                      onClick={() => removeLine(line.id)}
                    >
                      <LuTrash2 className="debit-remove-icon-desktop" />
                      <LuX className="debit-remove-icon-mobile" />
                    </button>
                  </div>
                ))}
                </CreditLinesViewport>
              </div>
              {errorsVisible && !amountValid && (
                <span className="debit-field-error">
                  Enter positive quantities and amounts with no more than two
                  decimal places.
                </span>
              )}
              <div className="debit-line-total-row">
                <span>
                  {credit
                    ? "Total credit"
                    : customer
                    ? "Total debit"
                    : "Total requested reduction"}
                </span>
                <strong>{formatMoney(total)}</strong>
              </div>
              {errorsVisible &&
                (credit || !customer) &&
                total > (selectedSource?.allowance || 0) && (
                  <span className="debit-field-error">
                    {credit ? "Credit amount exceeds the eligible credit allowance. Prior posted credits are accounted for separately from the outstanding balance." : "The reduction exceeds the remaining eligible allowance."}
                  </span>
                )}
              <button
                type="button"
                disabled={busy || form.lines.length >= 200}
                className="debit-add-line"
                onClick={addLine}
              >
                <LuPlus /> Add line item
              </button>
            </section>

            <section className="debit-form-section">
              <label htmlFor="debit-reason">Reason</label>
              <InvoiceSelect
                id="debit-reason"
                dark={controller.darkMode}
                aria-label="Reason"
                value={form.reason}
                onChange={(event) => {
                  const reason = event.target.value;
                  setForm((current) => ({
                    ...current,
                    reason,
                  }));
                }}
              >
                <option value="">Select a reason</option>
                {reasons.map((reason) => (
                  <option key={reason}>{reason}</option>
                ))}
              </InvoiceSelect>
              {fieldError("reason", "Choose a reason for this adjustment.")}
              {!customer && (
                <p className="debit-field-hint">
                  Choose a reason for the supplier request. This is retained
                  with the draft and shown in the supplier notification.
                </p>
              )}
            </section>

            <section className="debit-form-section">
              <h2>Tax treatment</h2>
              <div className="debit-info-note">
                <LuInfo aria-hidden="true" />
                <span>
                  Currency and tax treatment are inherited from{" "}
                  {selectedSource?.reference || "the linked document"}. No
                  additional GST is applied. The {credit ? "credit" : "debit"} amount of {formatMoney(total)}{" "}
                  is treated as the total adjustment.
                </span>
              </div>
            </section>

            <DebitPreview
              credit={credit}
              customer={customer}
              valid={isValid}
              selectedSource={selectedSource}
              lines={calculation.lines}
              total={total}
              currency={currency}
              formatMoney={formatMoney}
              responsivePlacement="tablet"
            />

            <section className="debit-form-section debit-notes-section">
              <div className="debit-notes-label">
                <h2>Internal notes</h2>
                <span>Optional</span>
                <span>Not visible to {customer ? "customer" : "supplier"}</span>
              </div>
              <textarea
                value={form.notes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
                placeholder="Add a private note for your records..."
                rows="3"
              />
            </section>

            <DebitPreview
              credit={credit}
              customer={customer}
              valid={isValid}
              selectedSource={selectedSource}
              lines={calculation.lines}
              total={total}
              currency={currency}
              formatMoney={formatMoney}
              responsivePlacement="mobile"
            />

            <footer className="debit-editor-footer">
              <p>
                {customer
                  ? credit
                    ? "Save draft leaves the invoice balance unchanged. Apply credit opens a confirmation before posting. Posted credits preserve audit history and cannot be deleted."
                    : "Save draft leaves the invoice balance unchanged. Issue debit note opens a confirmation before posting. Posted debit notes preserve audit history and cannot be deleted."
                  : "Save draft leaves the balance unchanged. Notify supplier reviews and sends a request without posting. A validated confirmation or linked supplier adjustment is required to post once as Applied."}
              </p>
              <EditorActions
                credit={credit}
                customer={customer}
                onCancel={cancelEditor}
                onSubmit={attemptAction}
                onSave={() => persist()}
                busy={busy}
                busyAction={busyAction}
                disabled={loadingDraft || draftLoadError}
                location="bottom"
              />
            </footer>
            </>}
          </fieldset>

          <DebitPreview
            credit={credit}
            customer={customer}
            valid={isValid}
            selectedSource={selectedSource}
            lines={calculation.lines}
            total={total}
            currency={currency}
            formatMoney={formatMoney}
          />
        </div>
      </main>

      {unsaved.dialog}
      {dialog === "issue" && (
        <Modal
          credit={credit}
          title={
            credit
              ? "Apply credit?"
              : customer
              ? "Review customer issue"
              : "Supplier request review"
          }
          onClose={() => !busy && setDialog("")}
          actions={
            <>
              <Button
                className="debit-secondary-button"
                disabled={busy}
                onClick={() => setDialog("")}
              >
                Cancel
              </Button>
              <Button
                className="debit-primary-button"
                disabled={busy || !acknowledged}
                onClick={approveIssue}
              >
                {busy && (
                  <CircularProgress
                    size={16}
                    color="inherit"
                    aria-hidden="true"
                  />
                )}
                {busy
                  ? credit
                    ? "Applying…"
                    : customer
                    ? "Issuing…"
                    : "Notifying supplier…"
                  : credit
                  ? "Apply credit"
                  : customer
                  ? "Issue debit note"
                  : "Notify supplier"}
              </Button>
            </>
          }
        >
          {actionError && (
            <p role="alert" className="debit-field-error">
              {actionError}{" "}
              {saved
                ? "Your saved draft is retained."
                : "Your entries are retained for review."}
            </p>
          )}
          <p>
            <strong>
              {selectedSource?.reference} · {selectedSource?.contact}
            </strong>
          </p>
          <p>{form.reason}</p>
          <p>
            {customer
              ? credit
                ? "This applies a credit to the linked invoice. The original invoice amount stays unchanged. Applied credits cannot be edited."
                : "This adds a supplementary charge to the linked invoice balance. The original invoice amount stays unchanged. Issued notes cannot be edited."
              : "This emails the supplier a request only. The payable stays unchanged; no supplier credit is assumed."}
          </p>
          <div className="note-confirm-balance">
            <p>
              <span>
                {customer ? "Outstanding balance" : "Recorded amount owed"}
              </span>
              <strong>{formatMoney(selectedSource?.balance)}</strong>
            </p>
            <p>
              <span>
                {credit
                  ? "Credit deduction"
                  : customer
                  ? "Customer charge"
                  : "Requested reduction"}
              </span>
              <strong>{formatMoney(total)}</strong>
            </p>
            <p>
              <span>
                {customer
                  ? "Projected remaining balance"
                  : "Projected amount owed if confirmed"}
              </span>
              <strong>
                {!customer && total > selectedSource.balance
                  ? "Unavailable until a valid reduction is confirmed"
                  : formatMoney(
                      customer && !credit
                        ? selectedSource.balance + total
                        : Math.max(0, selectedSource.balance - total)
                    )}
              </strong>
            </p>
          </div>
          {credit && total > (selectedSource?.balance || 0) && (
            <p>
              The excess {formatMoney(total - selectedSource.balance)} remains
              an unapplied customer credit. No refund is recorded.
            </p>
          )}
          <label className="debit-confirm-checkbox">
            <input
              type="checkbox"
              disabled={busy}
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            {credit ? "I confirm this credit should be applied." : "I have reviewed the linked document, amount and reason and confirm this action."}
          </label>
        </Modal>
      )}
      {dialog === "direction" && (
        <Modal
          title={`Switch to ${
            nextDirection === "supplier" ? "supplier" : "customer"
          }?`}
          onClose={() => setDialog("")}
          actions={
            <>
              <Button
                className="debit-secondary-button"
                onClick={() => setDialog("")}
              >
                Keep editing
              </Button>
              <Button className="debit-primary-button" onClick={confirmSwitch}>
                Discard and switch
              </Button>
            </>
          }
        >
          <p>
            Confirm before discarding unsaved{" "}
            {customer ? "customer" : "supplier"}
            -branch edits and switching to {nextDirection}. No balances change.
          </p>
          <p>
            The linked source, contact, reason, adjustment lines, private notes,
            GST selections and balance preview will be cleared. The last saved
            draft remains unchanged.
          </p>
        </Modal>
      )}
    </DashboardLayout>
  );
}
